import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";
import { Copy, RefreshCw, UserMinus, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppShell";
import { UsageGauge } from "@/components/UsageGauge";
import { UpgradeHint } from "@/components/UpgradeHint";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useAuth, useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const CopyField = ({ label, value, testId }) => {
  const { t } = useI18n();
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <div className="flex gap-2">
        <code data-testid={testId} className="flex-1 truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 font-mono text-sm">{value}</code>
        <Button variant="outline" className="h-12 gap-2" data-testid={`${testId}-copy`}
          onClick={() => navigator.clipboard.writeText(value).then(() => toast.success(t("mem.copied")))}><Copy className="w-4 h-4" />{t("mem.copy")}</Button>
      </div>
    </div>
  );
};

export default function Members() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { me, refresh } = useAuth();
  const { ws, plan, usage, canManage } = useWs();
  const showErr = useErrorToast();
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: () => api.get("/workspace/members").then((r) => r.data) });
  const rolesLocked = plan.limits.members === 1;
  const reload = () => { qc.invalidateQueries({ queryKey: ["members"] }); refresh(); };
  const act = (fn) => async (...a) => { try { await fn(...a); reload(); } catch (e) { showErr(e); } };
  const setRole = act((uid, role) => api.put(`/workspace/members/${uid}`, { role }));
  const remove = act((uid) => window.confirm(t("common.confirmDelete")) && api.delete(`/workspace/members/${uid}`));
  const regen = act(() => api.post("/workspace/invite/regenerate"));
  const [email, setEmail] = useState("");
  const inviteEmail = async (e) => {
    e.preventDefault();
    try { await api.post("/workspace/invite/email", { email, origin_url: window.location.origin }); toast.success(t("mem.inviteSent")); setEmail(""); }
    catch (err) { showErr(err); }
  };
  const leave = act(() => window.confirm(t("mem.leave") + " ?") && api.post("/workspace/leave"));

  return (
    <div className="max-w-3xl space-y-8">
      <PageHeader title={t("mem.title")} />
      <UsageGauge label={t("dash.members")} used={usage.members} limit={plan.limits.members} testId="members-gauge" />
      {canManage && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4" data-testid="invite-section">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-bold">{t("mem.invite")}</h2>
            <Button variant="ghost" size="sm" className="gap-2" onClick={regen} data-testid="invite-regenerate"><RefreshCw className="w-4 h-4" />{t("mem.regenerate")}</Button>
          </div>
          <p className="text-sm text-slate-500">{t("mem.inviteHint")}</p>
          <CopyField label={t("mem.inviteLink")} value={`${window.location.origin}/join/${ws.invite_code}`} testId="invite-link" />
          <CopyField label={t("mem.inviteCode")} value={ws.invite_code} testId="invite-code" />
          <form onSubmit={inviteEmail} className="flex gap-2 pt-2">
            <Input data-testid="invite-email-input" type="email" className="h-12" placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Button data-testid="invite-email-button" type="submit" className="h-12 gap-2"><Mail className="w-4 h-4" />{t("mem.inviteEmail")}</Button>
          </form>
        </section>
      )}
      {canManage && rolesLocked && <UpgradeHint text={t("mem.rolesLocked")} testId="roles-locked" />}
      <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="member-list">
        {members.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`member-row-${m.id}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">{m.name?.[0]?.toUpperCase()}</span>
            <span className="flex-1 min-w-0"><span className="block font-semibold truncate">{m.name}</span><span className="block text-xs text-slate-500 truncate">{m.email}</span></span>
            {canManage && m.role !== "owner" && m.id !== me.user.id ? (
              <>
                <Select value={m.role} onValueChange={(r) => setRole(m.id, r)}>
                  <SelectTrigger className="w-40 h-10" data-testid={`member-role-${m.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["admin", "member", "viewer"].map((r) => (
                    <SelectItem key={r} value={r} disabled={rolesLocked && r !== "member"}>{t(`role.${r}`)}</SelectItem>))}</SelectContent>
                </Select>
                <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(m.id)} data-testid={`member-remove-${m.id}`}><UserMinus className="w-4 h-4" /></Button>
              </>
            ) : <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold">{t(`role.${m.role}`)}</span>}
          </li>
        ))}
      </ul>
      {me.user.role !== "owner" && <Button variant="outline" className="text-red-600" onClick={leave} data-testid="leave-workspace">{t("mem.leave")}</Button>}
    </div>
  );
}
